/**
 * chainMonitoringUtils.js
 *
 * Funções utilitárias para adaptar dados do ChainVisualization
 * para o formato compacto usado no Executive Dashboard
 */

/**
 * Adapta dados completos do ChainVisualization para formato compacto
 * @param {Object} chainData - Dados processados do assembler
 * @param {number} maxTracks - Número máximo de tracks a exibir
 * @returns {Array} - Array simplificado de tracks
 */
export const adaptChainDataForCompactView = (chainData, maxTracks = 5) => {
  if (!chainData || !chainData.topologySubsections) {
    return [];
  }

  // 1. Flatten todas as topologySubsections em array único (com topology)
  const allTracks = flattenTopologySubsectionsWithTopology(chainData);

  // 2. Para cada track, consolidar steps baseado na topology
  const processedTracks = allTracks.map((track) => {
    const isIndirect = track.topology === "indireto";

    return {
      id: track.id,
      label: track.label,
      topology: track.topology,
      step1: extractStep1Data(track),
      channel: isIndirect ? consolidateChannelSteps(track) : null, // Só para indireto
      step3: extractStep3Data(track, isIndirect),
    };
  });

  // 3. Limitar quantidade
  return processedTracks.slice(0, maxTracks);
};

/**
 * Seleciona âncora padrão (primeira track com sellout)
 * @param {Object} chainData - Dados processados do assembler
 * @returns {string|null} - ID da track âncora
 */
export const selectDefaultAnchor = (chainData) => {
  if (!chainData || !chainData.topologySubsections) {
    return null;
  }

  const allTracks = flattenTopologySubsections(chainData);
  const trackWithSellout = allTracks.find((track) =>
    track.steps?.some((step) => step.type === "step5"),
  );

  return trackWithSellout?.id || null;
};

/**
 * Flatten de topologySubsections em array único de tracks (COM topology)
 * @param {Object} chainData - Dados processados do assembler
 * @returns {Array} - Array de tracks com topology
 */
const flattenTopologySubsectionsWithTopology = (chainData) => {
  const tracks = [];

  if (!chainData.topologySubsections) {
    return tracks;
  }

  chainData.topologySubsections.forEach((subsection) => {
    const topology = subsection.name?.toLowerCase() || "indireto"; // "Indireto" ou "Direto"

    if (subsection.tracks && Array.isArray(subsection.tracks)) {
      subsection.tracks.forEach((track) => {
        tracks.push({
          ...track,
          topology: topology, // Adiciona topology a cada track
        });
      });
    }
  });

  return tracks;
};

/**
 * Flatten de topologySubsections em array único de tracks (SEM topology - para compatibilidade)
 * @param {Object} chainData - Dados processados do assembler
 * @returns {Array} - Array de tracks
 */
const flattenTopologySubsections = (chainData) => {
  const tracks = [];

  if (!chainData.topologySubsections) {
    return tracks;
  }

  chainData.topologySubsections.forEach((subsection) => {
    if (subsection.tracks && Array.isArray(subsection.tracks)) {
      subsection.tracks.forEach((track) => {
        tracks.push(track);
      });
    }
  });

  return tracks;
};

/**
 * Extrai dados do Step 1 (Sell-in)
 * @param {Object} track - Track individual
 * @returns {Object} - Dados do step1 (discount, price)
 */
const extractStep1Data = (track) => {
  const step1 = track.steps?.find((s) => s.type === "step1");

  if (!step1) {
    return { discount: 0, price: 0 };
  }

  return {
    discount: step1.metrics?.discount || 0,
    price: step1.metrics?.priceGross || 0,
  };
};

/**
 * Consolida Step 2 (sempre usa step2.metrics)
 * @param {Object} track - Track individual
 * @returns {Object} - Dados consolidados (margin, price)
 */
const consolidateChannelSteps = (track) => {
  if (!track.steps) {
    return { margin: 0, price: 0 };
  }

  // Sempre usa step2
  const step2 = track.steps.find((s) => s.type === "step2");

  if (!step2) {
    return { margin: 0, price: 0 };
  }

  return {
    margin: step2.metrics?.margin || 0,
    price: step2.metrics?.priceGross || 0,
  };
};

/**
 * Extrai dados do Step 3 (margin) e Step 5 (price, relativity)
 * Para direto: usa step2 margin
 * Para indireto: usa step3 margin
 * @param {Object} track - Track individual
 * @param {boolean} isIndirect - Se é topology indireto
 * @returns {Object} - Dados do step3 (margin) e step5 (price, relativity)
 */
const extractStep3Data = (track, isIndirect = true) => {
  const step5 = track.steps?.find((s) => s.type === "step5");

  if (!step5) {
    return { margin: 0, price: 0, relativity: 100 };
  }

  let margin = 0;

  if (isIndirect) {
    // Indireto: usa step3 margin
    const step3 = track.steps?.find((s) => s.type === "step3");
    margin = step3?.metrics?.margin || 0;
  } else {
    // Direto: usa step2 margin
    const step2 = track.steps?.find((s) => s.type === "step2");
    margin = step2?.metrics?.margin || 0;
  }

  return {
    margin: margin,
    price: step5.metrics?.price || 0,
    relativity: step5.relativity || 100,
  };
};

/**
 * Formata valor numérico para exibição
 * @param {number} value - Valor a formatar
 * @param {number} decimals - Número de casas decimais
 * @returns {string} - Valor formatado
 */
export const formatValue = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  return Number(value).toFixed(decimals);
};

/**
 * Formata percentual para exibição
 * @param {number} value - Valor percentual
 * @returns {string} - Percentual formatado com símbolo %
 */
export const formatPercentage = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  return `${Number(value).toFixed(0)}%`;
};
